import { Component, type ErrorInfo, type ReactNode } from 'react';

import { AppSettings } from '@/constants';
import { captureError } from '@/services/analytics';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report error to PostHog
    captureError(error, errorInfo);

    // Log to console in development
    if (AppSettings.debug) {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className='mx-auto flex h-screen max-w-screen-xl flex-col items-center justify-center gap-4'>
            <span className='text-5xl'>😢</span>
            {AppSettings.debug && (
              <pre className='text-sm font-mono'>
                {JSON.stringify(this.state.error, null, 2)}
              </pre>
            )}
          </div>
        )
      );
    }

    return this.props.children;
  }
}
